import { Text, Group, Button, Checkbox, Stack } from "@mantine/core"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { ErrorText } from "../../../blocks/errors/error-alert"
import { usePostMutation } from "../../../typed-client/hooks/use-post-mutation"
import { LinkAnchor } from "../../app/-components/link-anchor"
import { DefaultUserNameTextInput } from "../../../-components/default-username-text-input"
import { DefaultPasswordInput } from "../../../-components/default-password-input"
import { DefaultError } from "../../../-components/error-context"
import { useInterval, useLocalStorage } from "@mantine/hooks"

export function LoginForm() {
  const queryClient = useQueryClient()
  const [persistent, setPersistent] = useLocalStorage({
    defaultValue: false,
    key: "login-persistent",
  })
  const form = useForm({
    initialValues: {
      userName: "",
      password: "",
      persistent: persistent,
    },
  })
  const search = useSearch({ from: "/authenticate/login" })

  const navigate = useNavigate()
  const { t } = useTranslation("app_server")

  const [lockSeconds, setLockSeconds] = React.useState<number | null>(null)
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)

  const { start, stop } = useInterval(() => {
    if (lockSeconds !== null && lockSeconds > 0) setLockSeconds(lockSeconds - 1)
    if (lockSeconds === 0) {
      stop()
      setLockSeconds(null)
    }
  }, 1000)
  const [displayTime, setDisplayTime] = React.useState<string>("")

  React.useEffect(() => {
    if (lockSeconds) {
      const time =
        Math.floor(lockSeconds / 3600)
          .toString()
          .padStart(2, "0") +
        ":" +
        Math.floor((lockSeconds % 3600) / 60)
          .toString()
          .padStart(2, "0") +
        ":" +
        (lockSeconds % 60).toString().padStart(2, "0")
      setDisplayTime(time)
    }
  }, [lockSeconds])

  React.useEffect(() => {
    setPersistent(form.values.persistent)
  }, [form.values.persistent])

  const {
    mutateAsync: login,
    isPending,
    isError,
  } = usePostMutation("/api/Authentication/Login", {
    onSuccess: (loginResponse) => {
      queryClient.clear()
      if (loginResponse.mfaRequired) {
        form.reset()
        if (loginResponse.mfaConfiguered) {
          navigate({
            to: "/authenticate/mfa/select-authentication-factor",
            search: { returnUrl: search.returnUrl },
          })
        } else {
          navigate({
            to: "/authenticate/mfa/create",
            search: { returnUrl: search.returnUrl },
          })
        }
      } else {
        notifications.show({
          color: "green",
          title: t("Success"),
          message: t("Authenticate_LoginForm_SuccessMessage"),
        })
        navigate({ to: search.returnUrl || "/app" })
      }
    },
    onError: (error: DefaultError) => {
      setErrorMessage(error.message)
      if (error.LockSeconds) {
        setLockSeconds(error.LockSeconds)

        start()
      }
    },
  })

  const handleSubmit = async (values: typeof form.values) => {
    await login(values)
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <DefaultUserNameTextInput
          autoFocus
          required
          {...form.getInputProps("userName")}
        />
        <DefaultPasswordInput {...form.getInputProps("password")} />
        <Checkbox
          label={t("Login_RememberMe")}
          {...form.getInputProps("persistent")}
        />
        <Group justify="space-between" mt="lg">
          <LinkAnchor
            to={"/authenticate/password-forgotten"}
            search={{ userName: form.values.userName }}
          >
            {t("ResetPassword")}
          </LinkAnchor>
        </Group>
        {isError ? <ErrorText mt="xs" error={errorMessage} /> : null}
        {lockSeconds ? (
          <Text c="red">{t("Login_Lock_Message", { time: displayTime })}</Text>
        ) : null}
        <Button type="submit" fullWidth mt="xl" loading={isPending}>
          {t("Login")}
        </Button>
      </Stack>
    </form>
  )
}
